"use client";

import ConfirmUpdatePlan from "@/components/ConfirmUpdatePlan";
import FailedPaymentWall from "@/components/FailedPaymentWall";
import NeedPaidPlan from "@/components/NeedPaidPlan";
import { PlanType } from "../const/planConst";
import { BillingHelper } from "./billing";
import { CANCELLED_USER_ERROR_CODE } from "./cancelledAccess";
import {
  FAILED_PAYMENT_WALL_ERROR_CODE,
  FailedPaymentWallAction,
} from "./failedPaymentWall";
import { ProfileModel } from "../types/models";
import { ReactElement } from "react";

export type PaymentRequiredBody = {
  error_code?: string;
  required_plan?: PlanType;
};

type NeedPaidPlanVariant =
  | "default"
  | "export_clip"
  | "download_clip"
  | "remove_watermark"
  | "video_limit_reached"
  | "clip_limit_reached"
  | "export_limit_reached"
  | "credit_limit_reached"
  | "upload_limit_reached"
  | "update_plan"
  | "auto_post"
  | "subscription_ended";

export const resolveUpgradeTargetPlan = (
  profile: ProfileModel | null | undefined,
  requiredPlan?: PlanType | null,
): PlanType => {
  if (requiredPlan) return requiredPlan;
  if (profile?.plan) {
    return BillingHelper.getNextPlan(profile.plan) ?? "pro-plus";
  }
  return "pro";
};

export const getPaymentRequiredDialog = ({
  errorCode,
  requiredPlan,
  profile,
  unpaidVariant = "download_clip",
  action,
}: {
  errorCode?: string;
  requiredPlan?: PlanType | null;
  profile?: ProfileModel | null;
  unpaidVariant?: NeedPaidPlanVariant;
  /** What was blocked, for the failed-payment wall's events. */
  action?: FailedPaymentWallAction;
}): { content: ReactElement; className?: string } => {
  // A failed renewal blocks paying users too (helpers/failedPaymentWall.ts):
  // the way out is fixing the card, not a plan.
  if (errorCode === FAILED_PAYMENT_WALL_ERROR_CODE) {
    return {
      content: <FailedPaymentWall action={action} />,
      className: "max-w-md",
    };
  }

  // A lapsed subscriber (helpers/cancelledAccess.ts) needs a new
  // subscription; ConfirmUpdatePlan would PATCH one that no longer exists.
  if (errorCode === CANCELLED_USER_ERROR_CODE) {
    return {
      content: <NeedPaidPlan variant="subscription_ended" />,
      className: "max-w-6xl",
    };
  }

  const targetPlan = resolveUpgradeTargetPlan(profile, requiredPlan);

  if (profile?.plan) {
    if (errorCode === "export_limit_reached") {
      return {
        content: (
          <ConfirmUpdatePlan variant="more_exports" targetPlan={targetPlan} />
        ),
      };
    }
    if (errorCode === "credit_limit_reached") {
      return {
        content: (
          <ConfirmUpdatePlan variant="more_credits" targetPlan={targetPlan} />
        ),
      };
    }
    if (errorCode === "upload_limit_reached") {
      return {
        content: (
          <ConfirmUpdatePlan
            variant="upload_fair_use"
            targetPlan={targetPlan}
          />
        ),
      };
    }
  }

  const needPaidVariant: NeedPaidPlanVariant =
    errorCode === "export_limit_reached"
      ? "export_limit_reached"
      : errorCode === "credit_limit_reached"
        ? "credit_limit_reached"
        : errorCode === "upload_limit_reached"
          ? "upload_limit_reached"
          : unpaidVariant;

  return {
    content: <NeedPaidPlan variant={needPaidVariant} />,
    className: "max-w-6xl",
  };
};

export const parsePaymentRequiredBody = async (
  response: Response,
): Promise<PaymentRequiredBody> => {
  try {
    return (await response.json()) as PaymentRequiredBody;
  } catch {
    return {};
  }
};
